import React, { useCallback, useRef, useState } from 'react';

import type { StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { RemixOptionKind } from '../remix/remix-overlay/types';

import type {
	AddRemixHistoryEntryPayload,
	AddRemixHistoryStackEntryPayload,
	ContentClassification,
	DynamicUiSource,
	RemixContentComponentFullProps,
	RemixFinalizeFailedPayload,
	RemixHistoryEntryForUpdate,
	RemixInsertSuccessPayload,
} from './types';

type EditorRemixContextType = {
	entryPoint?: string;
	smartCreationConfig?: RemixContentComponentFullProps['smartCreationConfig'];
	smartCreationContext?: RemixContentComponentFullProps['smartCreationContext'];

	isViewMode?: boolean;

	/** The type of remix that is currently selected. */
	dynamicUiType?: RemixOptionKind;
	setDynamicUiType: (type: RemixOptionKind | undefined) => void;

	/** The subtype of the remix that is currently selected. */
	dynamicUiSubtype?: string;
	setDynamicUiSubtype: (subtype: string | undefined) => void;

	dynamicUiSource?: DynamicUiSource;
	setDynamicUiSource: (source: DynamicUiSource | undefined) => void;

	/** The conversation ID of the remix conversation. */
	conversationId?: string;
	setConversationId: (conversationId: string | undefined) => void;

	/** The interaction source that triggered this remix (e.g. 'remixModalFromMediaInserts'). */
	interactionSource?: string;
	setInteractionSource: (interactionSource: string | undefined) => void;

	/**
	 * The aspect ratio selected for AI image generation (e.g. "16:9", "1:1").
	 * Set when the remix originates from the `ImageFromAIGeneration` component
	 * (via `remixModalMetadata.aspectRatio`). Propagated end-to-end to the editor
	 * insert-content bridge command so the inserted image can be sized to its
	 * intended container.
	 */
	aspectRatio?: string | null;
	setAspectRatio: (aspectRatio: string | null | undefined) => void;

	/** The final complete message that is returned from the remix. */
	finalMessage?: StoreAssistant;
	setFinalMessage: (message: StoreAssistant | undefined) => void;

	/**
	 * Normalized classification for the content currently being streamed into Remix.
	 * Classification is sourced from ACTION_CLASSIFICATION, while `data` is filled by
	 * the first content-specific ACTION_PART payload so insertions wait for stream output.
	 */
	contentClassification?: ContentClassification;
	setContentClassification: React.Dispatch<React.SetStateAction<ContentClassification | undefined>>;

	/** A callback that clears the editor-owned thinking decoration without completing the flow. */
	clearThinkingDecoration?: () => void;

	/** Replaces an early streaming whiteboard embed with its finalized canonical URL. */
	replaceWhiteboardEmbed?: (options: {
		contentId: string;
		finalizedUrl: string;
		localId?: string;
	}) => void;

	/** Drives the MAUI "visible to others" banner for a whiteboard embed; set by the Rovo remix finalizer. */
	setMauiEmbedFinalizeStatus?: (options: {
		contentId: string;
		status: 'suppressed' | 'needs-finalize';
	}) => void;

	/** A callback that is called when the thinking is complete. */
	setThinkingComplete?: (isComplete: boolean) => void;

	/** A callback that finalizes the active remix thinking lifecycle. */
	finishRemixThinking?: () => void;

	/** Whether the location picker is currently shown. */
	showLocationPicker: boolean;
	setShowLocationPicker: (show: boolean) => void;

	/**
	 * Adds an entry to the remix history. This is used to keep track of the different remixes
	 * that have been done in the editor and their associated conversation and message data.
	 * This allows the user to navigate back and forth between different remixes and see the associated conversation history for each remix.
	 */
	addHistoryEntry?: (options: AddRemixHistoryEntryPayload) => void;

	/**
	 * Adds an entry to the remix history stack.
	 */
	addHistoryStackEntry?: (options: AddRemixHistoryStackEntryPayload) => void;

	/**
	 * Fired by `useOnComplete` once a variant-picker update settles. Hosts wire
	 * it to `trackVariantUpdateComplete`; `id` matches the value the toolbar
	 * registered via `trackVariantUpdateStart` (image fieldId or MAUI app id).
	 */
	onVariantUpdateComplete?: (id: string) => void;

	/**
	 * Fired after a newly-created remix has been inserted into the editor.
	 * Product integrations can use this to run post-insert side effects.
	 */
	onRemixInsertSuccess?: (payload: RemixInsertSuccessPayload) => void;

	/**
	 * Fired when automatic finalization of a remixed whiteboard fails. Product
	 * integrations may take over the recovery UX and return `true` to signal the
	 * failure was handled.
	 */
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;

	/** Whether the remix is an update. */
	isUpdate?: boolean;
	/** The history entry for the update. */
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	/**
	 * Id of the final message displayed on the page when an update was initiated.
	 * Captured live in useUpdateRemix and read by the useRemixToAIFC dedup so the
	 * previously-displayed result is suppressed until the new variant response
	 * arrives. A ref (not state) so it is readable synchronously without a re-render,
	 * and because stored remix-history ids can be stale for BE-driven edits.
	 */
	remixUpdateSentinelRef: React.MutableRefObject<string | undefined>;
	/** The dynamic UI subtype for the update. */
	dynamicUiSubtypeForUpdate?: string;

	/** A callback that is called when the remix thinking complete animation is finished. */
	onRemixThinkingCompleteAnimationFinished?: () => void;

	/** Resets the context to the initial state. */
	resetContext: () => void;
};

export const EditorRemixContext: React.Context<EditorRemixContextType> =
	React.createContext<EditorRemixContextType>({
		entryPoint: undefined,
		smartCreationConfig: undefined,
		smartCreationContext: undefined,
		isViewMode: undefined,
		dynamicUiType: undefined,
		setDynamicUiType: (type: RemixOptionKind | undefined) => {},
		dynamicUiSubtype: undefined,
		setDynamicUiSubtype: (subtype: string | undefined) => {},
		dynamicUiSource: undefined,
		setDynamicUiSource: (source: DynamicUiSource | undefined) => {},
		conversationId: undefined,
		setConversationId: (conversationId: string | undefined) => {},
		interactionSource: undefined,
		setInteractionSource: (interactionSource: string | undefined) => {},
		aspectRatio: undefined,
		setAspectRatio: (aspectRatio: string | null | undefined) => {},
		finalMessage: undefined,
		setFinalMessage: (message: StoreAssistant | undefined) => {},
		contentClassification: undefined,
		setContentClassification: (
			classification: React.SetStateAction<ContentClassification | undefined>,
		) => {},
		clearThinkingDecoration: () => {},
		replaceWhiteboardEmbed: () => {},
		setMauiEmbedFinalizeStatus: () => {},
		setThinkingComplete: (isComplete: boolean) => {},
		finishRemixThinking: () => {},
		showLocationPicker: false,
		setShowLocationPicker: (show: boolean) => {},
		addHistoryEntry: (options: AddRemixHistoryEntryPayload) => {},
		addHistoryStackEntry: (options: AddRemixHistoryStackEntryPayload) => {},
		onVariantUpdateComplete: (id: string) => {},
		onRemixInsertSuccess: (payload: RemixInsertSuccessPayload) => {},
		onFinalizeFailed: undefined,
		isUpdate: undefined,
		historyEntryForUpdate: undefined,
		remixUpdateSentinelRef: { current: undefined },
		dynamicUiSubtypeForUpdate: undefined,
		onRemixThinkingCompleteAnimationFinished: () => {},
		resetContext: () => {},
	});

export const useEditorRemixContext = (): EditorRemixContextType => {
	return React.useContext(EditorRemixContext);
};

export const EditorRemixContextProvider = ({
	entryPoint,
	isViewMode,
	clearThinkingDecoration,
	replaceWhiteboardEmbed,
	setMauiEmbedFinalizeStatus,
	setThinkingComplete,
	finishRemixThinking,
	showLocationPicker = false,
	setShowLocationPicker = () => {},
	smartCreationConfig,
	smartCreationContext,
	addHistoryEntry,
	addHistoryStackEntry,
	onVariantUpdateComplete,
	onRemixInsertSuccess,
	onFinalizeFailed,
	isUpdate,
	historyEntryForUpdate,
	dynamicUiSubtypeForUpdate,
	onRemixThinkingCompleteAnimationFinished,
	children,
}: {
	entryPoint?: string;
	isViewMode?: boolean;
	clearThinkingDecoration?: () => void;
	replaceWhiteboardEmbed?: (options: {
		contentId: string;
		finalizedUrl: string;
		localId?: string;
	}) => void;
	setMauiEmbedFinalizeStatus?: (options: {
		contentId: string;
		status: 'suppressed' | 'needs-finalize';
	}) => void;
	setThinkingComplete?: (isComplete: boolean) => void;
	finishRemixThinking?: () => void;
	showLocationPicker?: boolean;
	setShowLocationPicker?: (showLocationPicker: boolean) => void;
	addHistoryEntry?: (options: AddRemixHistoryEntryPayload) => void;
	addHistoryStackEntry?: (options: AddRemixHistoryStackEntryPayload) => void;
	onVariantUpdateComplete?: (id: string) => void;
	isUpdate?: boolean;
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	dynamicUiSubtypeForUpdate?: string;
	smartCreationConfig?: RemixContentComponentFullProps['smartCreationConfig'];
	smartCreationContext?: RemixContentComponentFullProps['smartCreationContext'];
	onRemixInsertSuccess?: (payload: RemixInsertSuccessPayload) => void;
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;
	onRemixThinkingCompleteAnimationFinished?: () => void;
	children: React.ReactNode;
}): React.JSX.Element => {
	const [dynamicUiType, setDynamicUiType] = useState<RemixOptionKind | undefined>(undefined);
	const [dynamicUiSubtype, setDynamicUiSubtype] = useState<string | undefined>(undefined);
	const [dynamicUiSource, setDynamicUiSource] = useState<DynamicUiSource | undefined>(undefined);
	const [conversationId, setConversationId] = useState<string | undefined>(undefined);
	const [interactionSource, setInteractionSource] = useState<string | undefined>(undefined);
	const [aspectRatio, setAspectRatio] = useState<string | null | undefined>(undefined);
	const [finalMessage, setFinalMessage] = useState<StoreAssistant | undefined>(undefined);
	const [contentClassification, setContentClassification] = useState<
		ContentClassification | undefined
	>(undefined);
	const remixUpdateSentinelRef = useRef<string | undefined>(undefined);

	const resetContext = useCallback(() => {
		setDynamicUiType(undefined);
		setDynamicUiSubtype(undefined);
		setDynamicUiSource(undefined);
		setConversationId(undefined);
		setInteractionSource(undefined);
		setAspectRatio(undefined);
		setFinalMessage(undefined);
		setContentClassification(undefined);
		remixUpdateSentinelRef.current = undefined;
	}, []);

	return (
		<EditorRemixContext.Provider
			value={{
				entryPoint,
				smartCreationConfig,
				smartCreationContext,
				isViewMode,
				dynamicUiType,
				setDynamicUiType,
				dynamicUiSubtype,
				setDynamicUiSubtype,
				dynamicUiSource,
				setDynamicUiSource,
				conversationId,
				setConversationId,
				interactionSource,
				setInteractionSource,
				aspectRatio,
				setAspectRatio,
				finalMessage,
				setFinalMessage,
				contentClassification,
				setContentClassification,
				clearThinkingDecoration,
				replaceWhiteboardEmbed,
				setMauiEmbedFinalizeStatus,
				setThinkingComplete,
				finishRemixThinking,
				showLocationPicker,
				setShowLocationPicker,
				addHistoryEntry,
				addHistoryStackEntry,
				onVariantUpdateComplete,
				onRemixInsertSuccess,
				onFinalizeFailed,
				isUpdate,
				historyEntryForUpdate,
				remixUpdateSentinelRef,
				dynamicUiSubtypeForUpdate,
				onRemixThinkingCompleteAnimationFinished,
				resetContext,
			}}
		>
			{children}
		</EditorRemixContext.Provider>
	);
};
